import { useCallback, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { ChevronDown, ChevronUp, Eye, EyeOff, GripVertical, Power, PowerOff } from 'lucide-react';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';
import type { MenuItemState, MenuNode } from '../../hooks/useMenuConfig';
import IconGlyph from './IconGlyph';
import IconPickerModal from './IconPickerModal';

const WINE = '#9F1239';

interface Props {
  item: MenuNode;
  parentId: string;
  parentIndex: number;
  index: number;
  totalSubItems: number;
  state: MenuItemState;
  moveSubItem: (parentIndex: number, dragIndex: number, hoverIndex: number) => void;
  onToggleEnabled: () => void;
  onToggleHidden: () => void;
  onIconChange: (icon: string) => void;
  onSetSequence: (parentId: string, subMenuId: string, seq: number) => void;
  onMoveBy: (parentId: string, subMenuId: string, delta: -1 | 1) => void;
}

/** Draggable sub-menu row inside an expanded menu card. */
export default function SubItemRow({
  item,
  parentId,
  parentIndex,
  index,
  totalSubItems,
  state,
  moveSubItem,
  onToggleEnabled,
  onToggleHidden,
  onIconChange,
  onSetSequence,
  onMoveBy,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [showIconPicker, setShowIconPicker] = useState(false);

  const [{ isDragging }, drag] = useDrag({
    type: 'SUB_MENU_ITEM',
    item: () => ({ index, parentIndex, type: 'sub' }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'SUB_MENU_ITEM',
    hover: (dragged: { index: number; parentIndex: number }) => {
      if (!ref.current) return;
      if (dragged.parentIndex !== parentIndex || dragged.index === index) return;
      moveSubItem(parentIndex, dragged.index, index);
      dragged.index = index;
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;
      drag(drop(node));
    },
    [drag, drop],
  );

  const icon = state.icon || item.icon;
  const { enabled, hidden } = state;

  return (
    <>
      <div
        ref={setRef}
        className="flex items-center gap-2.5 rounded-lg px-3 py-2 transition-colors"
        style={{
          background: SOVEREIGN.inputBg,
          border: `1px solid ${isOver ? SOVEREIGN.brand : SOVEREIGN.barTrackBorder}`,
          opacity: isDragging ? 0.55 : hidden ? 0.7 : 1,
        }}
      >
        <span className="cursor-move shrink-0" style={{ color: SOVEREIGN.kpiCaption }}>
          <GripVertical size={13} />
        </span>

        {/* Sequence stepper */}
        <span className="flex shrink-0 flex-col items-center">
          <button
            type="button"
            onClick={() => onMoveBy(parentId, item.id, -1)}
            disabled={index === 0}
            className="p-0.5 disabled:opacity-30"
            style={{ color: SOVEREIGN.bottle }}
            title="Move up"
          >
            <ChevronUp size={11} />
          </button>
          <input
            type="number"
            min={1}
            max={totalSubItems}
            value={index + 1}
            onChange={(e) => {
              const seq = Number.parseInt(e.target.value, 10);
              if (!Number.isNaN(seq)) onSetSequence(parentId, item.id, seq);
            }}
            className="w-8 rounded text-center text-[11px] font-bold tabular-nums focus:outline-none"
            style={{
              background: 'rgba(201,169,97,0.12)',
              border: `1px solid ${SOVEREIGN.barTrackBorder}`,
              color: SOVEREIGN.title,
            }}
            aria-label={`Position of ${item.name}`}
          />
          <button
            type="button"
            onClick={() => onMoveBy(parentId, item.id, 1)}
            disabled={index === totalSubItems - 1}
            className="p-0.5 disabled:opacity-30"
            style={{ color: SOVEREIGN.bottle }}
            title="Move down"
          >
            <ChevronDown size={11} />
          </button>
        </span>

        {/* Icon */}
        <button
          type="button"
          onClick={() => setShowIconPicker(true)}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors"
          style={{
            background: SOVEREIGN.cardBg,
            border: `1px solid ${SOVEREIGN.inputBorder}`,
          }}
          title="Change icon"
        >
          <IconGlyph icon={icon} className="w-3.5 h-3.5" color={SOVEREIGN.bottle} />
        </button>

        {/* Name + path */}
        <span className="min-w-0 flex-1">
          <span
            className="block truncate text-[12.5px] font-semibold"
            style={{
              color: enabled ? SOVEREIGN.ink : SOVEREIGN.kpiCaption,
              textDecoration: hidden ? 'line-through' : 'none',
            }}
          >
            {item.name}
          </span>
          <span className="block truncate text-[10.5px]" style={{ color: SOVEREIGN.kpiCaption }}>
            {item.path}
          </span>
        </span>

        {/* Status chips */}
        {!enabled && (
          <span
            className="shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider"
            style={{ background: 'rgba(124,138,130,0.14)', color: SOVEREIGN.kpiCaption }}
          >
            Disabled
          </span>
        )}
        {hidden && (
          <span
            className="shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider"
            style={{ background: 'rgba(159,18,57,0.10)', color: WINE }}
          >
            Hidden
          </span>
        )}

        {/* Toggles */}
        <span className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onToggleEnabled}
            className="rounded-md p-1.5 transition-colors"
            style={{
              background: enabled ? 'rgba(51,146,116,0.10)' : 'rgba(124,138,130,0.12)',
              color: enabled ? SOVEREIGN.bottle : SOVEREIGN.kpiCaption,
              border: `1px solid ${enabled ? 'rgba(51,146,116,0.25)' : SOVEREIGN.barTrackBorder}`,
            }}
            title={enabled ? 'Disable' : 'Enable'}
          >
            {enabled ? <Power size={12} /> : <PowerOff size={12} />}
          </button>
          <button
            type="button"
            onClick={onToggleHidden}
            className="rounded-md p-1.5 transition-colors"
            style={{
              background: hidden ? 'rgba(159,18,57,0.08)' : 'rgba(201,169,97,0.12)',
              color: hidden ? WINE : '#8A5A08',
              border: `1px solid ${hidden ? 'rgba(159,18,57,0.22)' : 'rgba(201,169,97,0.35)'}`,
            }}
            title={hidden ? 'Show in sidebar' : 'Hide from sidebar'}
          >
            {hidden ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        </span>
      </div>

      {showIconPicker && (
        <IconPickerModal
          currentIcon={icon}
          onSelect={onIconChange}
          onClose={() => setShowIconPicker(false)}
        />
      )}
    </>
  );
}
